/**
 * Tracky (web.tracky.ma) adapter.
 *
 * Tracky is NOT a GPSWOX/Traccar install — it's a modern custom SPA with a
 * JSON + JWT REST API. This adapter logs in (JSON), gets a bearer token, then
 * lists vehicles and normalises them to the app's standard device shape:
 *   { id, name, uniqueId, status, position:{latitude,longitude,speed,course,...}, raw }
 *
 * Because the exact endpoints aren't documented, we try the common modern REST
 * patterns and flexibly extract token / vehicles / coordinates from many field
 * names. All requests go through our own /proxy (CORS + bad-SSL safe).
 */

const SERVER_PROXY = (() => {
  if (typeof window !== 'undefined') {
    const { protocol, host } = window.location;
    if (!protocol.startsWith('file')) return `${protocol}//${host}/proxy/`;
  }
  return null;
})();

const TIMEOUT = 30000;

// Low-level request through our proxy (JSON capable, forwards bearer token).
async function trackyFetch(absoluteUrl, { method = 'GET', token, body } = {}) {
  const url = SERVER_PROXY ? `${SERVER_PROXY}${absoluteUrl}` : absoluteUrl;
  const controller = new AbortController();
  const to = setTimeout(() => controller.abort(), TIMEOUT);
  try {
    const headers = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const opts = { method, headers, signal: controller.signal };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    const res = await fetch(url, opts);
    const text = await res.text();
    let json = null;
    try { json = text && (text.trim().startsWith('{') || text.trim().startsWith('[')) ? JSON.parse(text) : null; } catch (_) {}
    return { ok: res.ok, status: res.status, text, json };
  } finally {
    clearTimeout(to);
  }
}

// Deep-search an object for the first defined value among candidate keys.
const pick = (obj, keys) => {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
  }
  return undefined;
};
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : undefined; };

const LOGIN_PATHS = ['/api/auth/login', '/api/login', '/api/v1/auth/login', '/api/users/login', '/auth/login', '/api/v1/login', '/api/account/login'];
const VEHICLE_PATHS = ['/api/vehicles', '/api/devices', '/api/v1/vehicles', '/api/v1/devices', '/api/cars', '/api/vehicle/list', '/api/positions', '/api/units'];

function extractToken(json) {
  if (!json || typeof json !== 'object') return null;
  const direct = pick(json, ['token', 'access_token', 'accessToken', 'jwt', 'auth_token', 'authToken', 'id_token']);
  if (direct) return direct;
  // nested under data / result / user
  for (const k of ['data', 'result', 'user', 'payload']) {
    if (json[k] && typeof json[k] === 'object') {
      const t = pick(json[k], ['token', 'access_token', 'accessToken', 'jwt', 'auth_token', 'authToken']);
      if (t) return t;
    }
  }
  return null;
}

function extractVehicleArray(json) {
  if (Array.isArray(json)) return json;
  if (!json || typeof json !== 'object') return [];
  const cand = pick(json, ['data', 'vehicles', 'items', 'devices', 'units', 'results', 'list', 'records']);
  if (Array.isArray(cand)) return cand;
  // data.data / data.vehicles
  if (json.data && typeof json.data === 'object') {
    const inner = pick(json.data, ['data', 'vehicles', 'items', 'devices', 'units', 'list']);
    if (Array.isArray(inner)) return inner;
  }
  return [];
}

function getCoords(v) {
  // Direct
  let lat = num(pick(v, ['lat', 'latitude', 'Latitude', 'last_lat']));
  let lng = num(pick(v, ['lng', 'lon', 'long', 'longitude', 'Longitude', 'last_lng', 'last_lon']));
  if (lat !== undefined && lng !== undefined) return { lat, lng };
  // Nested position-like objects
  for (const k of ['lastPosition', 'last_position', 'position', 'location', 'last_location', 'gps', 'lastLocation', 'currentPosition', 'coords']) {
    const p = v[k];
    if (p && typeof p === 'object') {
      lat = num(pick(p, ['lat', 'latitude', 'Latitude']));
      lng = num(pick(p, ['lng', 'lon', 'long', 'longitude', 'Longitude']));
      if (lat !== undefined && lng !== undefined) return { lat, lng, src: p };
    }
  }
  return { lat: undefined, lng: undefined };
}

function normalizeVehicle(v) {
  const { lat, lng, src } = getCoords(v);
  const posObj = src || v;
  const speed = num(pick(posObj, ['speed', 'velocity', 'spd'])) || num(pick(v, ['speed', 'velocity'])) || 0;
  const course = num(pick(posObj, ['course', 'heading', 'direction', 'bearing', 'angle'])) || 0;
  const time = pick(posObj, ['time', 'timestamp', 'fixTime', 'deviceTime', 'updated_at', 'date', 'lastUpdate', 'gps_time'])
    || pick(v, ['time', 'timestamp', 'updated_at', 'lastUpdate']);
  const id = pick(v, ['id', 'device_id', 'deviceId', 'imei', 'uniqueId', 'unique_id', 'serial', '_id']);
  const name = pick(v, ['name', 'plate', 'matricule', 'immatriculation', 'registration', 'label', 'title', 'vehicle_name', 'plateNumber']) || `Device ${id}`;
  const imei = pick(v, ['imei', 'unique_id', 'uniqueId', 'serial', 'sim']);
  const onlineRaw = pick(v, ['online', 'status', 'state', 'isOnline', 'connection']);
  const offline = String(onlineRaw).toLowerCase() === 'offline' || onlineRaw === false || onlineRaw === 0;

  return {
    id,
    name,
    uniqueId: String(imei || id),
    status: offline ? 'offline' : 'online',
    lastUpdate: time,
    odometer: num(pick(v, ['odometer', 'mileage', 'totalDistance', 'total_distance'])) ?? null,
    sensors: [],
    raw: v,
    position: (lat !== undefined && lng !== undefined) ? {
      latitude: lat,
      longitude: lng,
      speed: speed || 0,
      course: course || 0,
      altitude: num(pick(posObj, ['altitude', 'alt'])) || 0,
      address: pick(posObj, ['address', 'location_name']) || '',
      fixTime: time,
      attributes: {},
    } : null,
  };
}

export async function trackyLogin(url, email, password) {
  const base = (url || 'https://web.tracky.ma').replace(/\/$/, '');
  const payloads = [
    { email, password },
    { login: email, password },
    { username: email, password },
    { email, password, rememberMe: true },
  ];
  let lastStatus = 0, lastBody = '';
  for (const path of LOGIN_PATHS) {
    for (const body of payloads) {
      let r;
      try { r = await trackyFetch(`${base}${path}`, { method: 'POST', body }); }
      catch (e) { lastBody = e.message; continue; }
      lastStatus = r.status; lastBody = (r.text || '').slice(0, 160);
      if (r.status === 404 || r.status === 405) break; // wrong path → next path
      if (r.ok && r.json) {
        const token = extractToken(r.json);
        if (token) return { success: true, token, base };
      }
    }
  }
  return { success: false, error: `Échec login Tracky (dernier statut ${lastStatus})`, details: lastBody };
}

export async function trackyFetchAll(settings) {
  const url = (settings.traccarUrl || 'https://web.tracky.ma');
  const email = (settings.traccarEmail || '').trim();
  const password = settings.traccarPassword || '';
  if (!url || !email || !password) return { success: false, error: 'GPS Tracky non configuré (URL, email ou mot de passe)' };

  const login = await trackyLogin(url, email, password);
  if (!login.success) return { success: false, error: login.error, errorCode: 'AUTH', details: login.details };

  // Try the vehicle endpoints until one returns an array.
  let vehicles = null, lastStatus = 0;
  for (const path of VEHICLE_PATHS) {
    let r;
    try { r = await trackyFetch(`${login.base}${path}`, { token: login.token }); }
    catch (_) { continue; }
    lastStatus = r.status;
    if (r.ok && r.json) {
      const arr = extractVehicleArray(r.json);
      if (arr.length) { vehicles = arr; break; }
      // Some APIs return an empty array legitimately — accept the first 200 with array shape.
      if (Array.isArray(r.json) || (r.json && (r.json.data || r.json.vehicles))) { vehicles = arr; break; }
    }
  }
  if (vehicles === null) return { success: false, error: `Connexion OK mais liste véhicules introuvable (statut ${lastStatus})`, errorCode: 'NO_DEVICES' };
  if (vehicles.length === 0) return { success: false, error: 'Connexion OK mais aucun véhicule sur ce compte Tracky', errorCode: 'NO_DEVICES' };

  return { success: true, data: vehicles.map(normalizeVehicle).filter(d => d.id) };
}
